import React, { useState, useEffect, useCallback } from 'react';
import { BrainCircuit, Loader, MessageSquare, Send, AlertTriangle } from 'lucide-react';
import { BibleAI } from '../lib/ai';

interface InlineAIAssistantProps {
  context: string;
  reference: string;
  initialQuestion?: string | null;
  onQuestionAnswered?: () => void;
}

interface Message {
  type: 'user' | 'assistant';
  content: string;
  error?: boolean;
}

export function InlineAIAssistant({ 
  context, 
  reference,
  initialQuestion,
  onQuestionAnswered 
}: InlineAIAssistantProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(true);
  const [processedQuestions] = useState(new Set<string>());
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const submitQuestion = useCallback(async (questionText: string, retry: number = 0) => {
    if (!questionText.trim() || loading) return;
    
    // Don't process duplicate questions unless it's a retry
    if (retry === 0 && processedQuestions.has(questionText)) return;
    if (retry === 0) processedQuestions.add(questionText);

    setLoading(true);
    setError(null);
    
    // Only add user message on first attempt, not retries
    if (retry === 0) {
      setMessages(prev => [...prev, { type: 'user', content: questionText }]);
    }

    try {
      const ai = await BibleAI.getInstance();
      const response = await ai.answerQuestion(questionText, context);
      
      if (!mounted) return;

      if (response.error) {
        if (retry < MAX_RETRIES) {
          // Wait and retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retry)));
          setRetryCount(retry + 1);
          return submitQuestion(questionText, retry + 1);
        }
        throw new Error(response.error);
      }

      if (!response.answer) {
        throw new Error('No answer received');
      }

      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: response.answer,
        error: false
      }]);
      
      if (retry === 0) onQuestionAnswered?.();
      setRetryCount(0);
    } catch (err) {
      if (mounted) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get an answer';
        setMessages(prev => [...prev, { 
          type: 'assistant', 
          content: 'I apologize, but I encountered an error. Please try asking your question again.',
          error: true
        }]);
        setError(errorMessage);
      }
    } finally {
      if (mounted) {
        setLoading(false);
      }
    }
  }, [context, loading, onQuestionAnswered, mounted, processedQuestions, MAX_RETRIES]);

  // Process initial question when component mounts or when it changes
  useEffect(() => {
    if (initialQuestion && mounted && !loading) {
      submitQuestion(initialQuestion);
    }
  }, [initialQuestion, submitQuestion, mounted, loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || loading) return;

    const userQuestion = question.trim();
    setQuestion('');
    await submitQuestion(userQuestion);
  };

  const handleRetry = async (questionText: string) => {
    processedQuestions.delete(questionText);
    setMessages(prev => prev.filter(msg => msg.error !== true));
    await submitQuestion(questionText);
  };

  return (
    <div className="scripture-card md:sticky md:top-24">
      <div className="flex items-center gap-2 mb-4">
        <BrainCircuit className="text-gold-400" size={24} />
        <h2 className="text-xl font-semibold text-white">Study Assistant</h2>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask a question about this passage..."
          className="input-primary flex-1"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !question.trim()}
          className="btn-primary !px-3"
        >
          {loading ? (
            <Loader className="animate-spin" size={20} />
          ) : (
            <Send size={20} />
          )}
        </button>
      </form>

      <div className="max-h-[calc(100vh-16rem)] overflow-y-auto space-y-4">
        {messages.length === 0 ? (
          <div className="text-white/60 text-center py-8">
            <MessageSquare className="mx-auto mb-3 text-white/40" size={32} />
            <p>Ask any question about {reference}</p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className={`p-3 rounded-lg ${
                message.type === 'user'
                  ? 'bg-white/10 ml-8'
                  : message.error 
                    ? 'bg-red-500/10 mr-8'
                    : 'bg-gold-400/10 mr-8'
              }`}
            >
              <p className="text-white/90">{message.content}</p>
              {message.error && (
                <button
                  onClick={() => handleRetry(messages[index - 1]?.content || '')}
                  className="mt-2 text-sm text-red-400 hover:text-red-300 flex items-center gap-1"
                >
                  <AlertTriangle size={14} />
                  <span>Retry question</span>
                </button>
              )}
            </div>
          ))
        )}

        {error && !messages.some(m => m.error) && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}